import { HeartPulse, Activity, Brain, FileText, Thermometer, Stethoscope, Users, AlertCircle } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Hero } from '../../components/Hero';

export function Dysautonomia() {
  return (
    <div>
      <Hero
        title="Dysautonomia Treatment"
        description="Expert diagnosis and comprehensive care for autonomic nervous system disorders, utilizing advanced diagnostic testing and personalized treatment approaches to improve quality of life."
        image="https://images.unsplash.com/photo-1631217868264-e5b90bb7e133?auto=format&fit=crop&q=80"
      />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid md:grid-cols-2 gap-12 mb-16">
          <div>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Expert Dysautonomia Care</h2>
            <p className="text-gray-600 mb-6">
              Dysautonomia encompasses a group of complex conditions affecting the autonomic nervous 
              system (ANS), which controls involuntary body functions such as heart rate, blood 
              pressure, digestion, temperature regulation, and more. When the ANS doesn't function 
              properly, it can cause a wide range of symptoms affecting multiple body systems.
            </p>
            <p className="text-gray-600">
              Our clinic specializes in diagnosing and treating various forms of dysautonomia using 
              state-of-the-art diagnostic tools and comprehensive treatment approaches. We understand 
              that each patient's experience with dysautonomia is unique, and we tailor our treatment 
              plans to address individual symptoms and needs.
            </p>
          </div>
          <div className="bg-gradient-cool rounded-lg p-8 shadow-glow">
            <h3 className="text-xl font-semibold text-gray-900 mb-4">Types of Dysautonomia We Treat</h3>
            <ul className="space-y-4">
              <li className="flex items-start">
                <HeartPulse className="h-6 w-6 text-blue-600 mr-3 mt-1" />
                <div>
                  <h4 className="font-semibold text-gray-900">POTS (Postural Orthostatic Tachycardia Syndrome)</h4>
                  <p className="text-gray-600">A condition characterized by excessive heart rate increase upon standing, often accompanied by dizziness, fatigue, and brain fog</p>
                </div>
              </li>
              <li className="flex items-start">
                <Activity className="h-6 w-6 text-blue-600 mr-3 mt-1" />
                <div>
                  <h4 className="font-semibold text-gray-900">Orthostatic Hypotension</h4>
                  <p className="text-gray-600">Significant blood pressure drop when standing, causing dizziness, fainting, and falls</p>
                </div>
              </li>
              <li className="flex items-start">
                <Brain className="h-6 w-6 text-blue-600 mr-3 mt-1" />
                <div>
                  <h4 className="font-semibold text-gray-900">Multiple System Atrophy</h4>
                  <p className="text-gray-600">A rare, progressive disorder affecting multiple body systems with autonomic dysfunction</p>
                </div>
              </li>
              <li className="flex items-start">
                <AlertCircle className="h-6 w-6 text-blue-600 mr-3 mt-1" />
                <div>
                  <h4 className="font-semibold text-gray-900">Autonomic Neuropathy</h4>
                  <p className="text-gray-600">Damage to autonomic nerves affecting various organ systems</p>
                </div>
              </li>
            </ul>
          </div>
        </div>

        <div className="mb-16">
          <h2 className="text-2xl font-bold text-gray-900 mb-8">Common Symptoms</h2>
          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-white rounded-lg p-6 shadow-md hover:shadow-glow transition-shadow">
              <div className="mb-6">
                <img
                  src="https://images.unsplash.com/photo-1576091160399-112ba8d25d1d?auto=format&fit=crop&q=80"
                  alt="Cardiovascular symptoms"
                  className="w-full h-40 object-cover rounded-lg mb-4"
                />
              </div>
              <HeartPulse className="h-8 w-8 text-blue-600 mb-4" />
              <h3 className="text-lg font-semibold text-gray-900 mb-3">Cardiovascular</h3>
              <ul className="space-y-2 text-gray-600">
                <li>• Rapid/slow heart rate</li>
                <li>• Blood pressure fluctuations</li>
                <li>• Chest pain</li>
                <li>• Poor blood circulation</li>
                <li>• Exercise intolerance</li>
              </ul>
            </div>
            <div className="bg-white rounded-lg p-6 shadow-md hover:shadow-glow transition-shadow">
              <div className="mb-6">
                <img
                  src="https://images.unsplash.com/photo-1559757175-5700dde675bc?auto=format&fit=crop&q=80"
                  alt="Neurological symptoms"
                  className="w-full h-40 object-cover rounded-lg mb-4"
                />
              </div>
              <Brain className="h-8 w-8 text-blue-600 mb-4" />
              <h3 className="text-lg font-semibold text-gray-900 mb-3">Neurological</h3>
              <ul className="space-y-2 text-gray-600">
                <li>• Brain fog</li>
                <li>• Dizziness</li>
                <li>• Fainting (syncope)</li>
                <li>• Headaches</li>
                <li>• Sleep disturbances</li>
              </ul>
            </div>
            <div className="bg-white rounded-lg p-6 shadow-md hover:shadow-glow transition-shadow">
              <div className="mb-6">
                <img
                  src="https://images.unsplash.com/photo-1631217868264-e5b90bb7e133?auto=format&fit=crop&q=80"
                  alt="Other symptoms"
                  className="w-full h-40 object-cover rounded-lg mb-4"
                />
              </div>
              <Thermometer className="h-8 w-8 text-blue-600 mb-4" />
              <h3 className="text-lg font-semibold text-gray-900 mb-3">Other Systems</h3>
              <ul className="space-y-2 text-gray-600">
                <li>• Temperature regulation issues</li>
                <li>• Digestive problems</li>
                <li>• Excessive sweating</li>
                <li>• Bladder dysfunction</li>
                <li>• Chronic fatigue</li>
              </ul>
            </div>
          </div>
        </div>
        
        <div className="bg-gradient-healing rounded-lg p-8 mb-16 shadow-glow">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">Our Diagnostic Approach</h2>
          <div className="grid md:grid-cols-2 gap-8">
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Advanced Testing</h3>
              <ul className="space-y-3 text-gray-600">
                <li className="flex items-start">
                  <Stethoscope className="h-5 w-5 text-blue-600 mr-2 mt-1" />
                  <span>VitalScan ANS Testing for comprehensive autonomic function assessment</span>
                </li>
                <li className="flex items-start">
                  <Activity className="h-5 w-5 text-blue-600 mr-2 mt-1" />
                  <span>Tilt Table Testing to evaluate orthostatic intolerance</span>
                </li>
                <li className="flex items-start">
                  <HeartPulse className="h-5 w-5 text-blue-600 mr-2 mt-1" />
                  <span>24-hour heart rate and blood pressure monitoring</span>
                </li>
                <li className="flex items-start">
                  <Brain className="h-5 w-5 text-blue-600 mr-2 mt-1" />
                  <span>Quantitative sudomotor axon reflex testing (QSART)</span>
                </li>
              </ul>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Evaluation Process</h3>
              <ul className="space-y-3 text-gray-600">
                <li className="flex items-start">
                  <FileText className="h-5 w-5 text-blue-600 mr-2 mt-1" />
                  <span>Detailed medical history and symptom analysis</span>
                </li>
                <li className="flex items-start">
                  <Users className="h-5 w-5 text-blue-600 mr-2 mt-1" />
                  <span>Physical examination focused on autonomic function</span>
                </li>
                <li className="flex items-start">
                  <Brain className="h-5 w-5 text-blue-600 mr-2 mt-1" />
                  <span>Neurological assessment</span>
                </li>
                <li className="flex items-start">
                  <Activity className="h-5 w-5 text-blue-600 mr-2 mt-1" />
                  <span>Functional capacity evaluation</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
        
        <div className="mb-16">
          <h2 className="text-2xl font-bold text-gray-900 mb-8">Comprehensive Treatment Approach</h2>
          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-white rounded-lg p-6 shadow-md hover:shadow-glow transition-shadow">
              <div className="mb-6">
                <img
                  src="https://images.unsplash.com/photo-1587854692152-cbe660dbde88?auto=format&fit=crop&q=80"
                  alt="Medication Management"
                  className="w-full h-40 object-cover rounded-lg"
                />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-3">Medication Management</h3>
              <p className="text-gray-600">
                Customized medication protocols to address specific symptoms, including:
              </p>
              <ul className="mt-3 space-y-2 text-gray-600">
                <li>• Blood pressure regulation</li>
                <li>• Heart rate control</li>
                <li>• Volume expansion</li>
                <li>• Symptom-specific treatments</li>
              </ul>
            </div>
            <div className="bg-white rounded-lg p-6 shadow-md hover:shadow-glow transition-shadow">
              <div className="mb-6">
                <img
                  src="https://images.unsplash.com/photo-1571019613576-2b22c76fd955?auto=format&fit=crop&q=80"
                  alt="Lifestyle Modifications"
                  className="w-full h-40 object-cover rounded-lg"
                />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-3">Lifestyle Modifications</h3>
              <p className="text-gray-600">
                Evidence-based strategies including:
              </p>
              <ul className="mt-3 space-y-2 text-gray-600">
                <li>• Hydration protocols</li>
                <li>• Dietary adjustments</li>
                <li>• Exercise programs</li>
                <li>• Sleep hygiene</li>
              </ul>
            </div>
            <div className="bg-white rounded-lg p-6 shadow-md hover:shadow-glow transition-shadow">
              <div className="mb-6">
                <img
                  src="https://images.unsplash.com/photo-1666214280557-f1b5022eb634?auto=format&fit=crop&q=80"
                  alt="Support Services"
                  className="w-full h-40 object-cover rounded-lg"
                />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-3">Support Services</h3>
              <p className="text-gray-600">
                Comprehensive support including:
              </p>
              <ul className="mt-3 space-y-2 text-gray-600">
                <li>• Physical therapy</li>
                <li>• Occupational therapy</li>
                <li>• Psychological support</li>
                <li>• Support group resources</li>
              </ul>
            </div>
          </div>
        </div>

        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Take Control of Your Health</h2>
          <p className="text-gray-600 mb-8 max-w-2xl mx-auto">
            Living with dysautonomia can be challenging, but with proper diagnosis and treatment, 
            many patients experience significant improvement in their symptoms and quality of life. 
            Our experienced team is here to help you understand and manage your condition effectively.
          </p>
          <div className="flex justify-center gap-4">
            <Link
              to="/contact"
              className="bg-blue-600 text-white px-8 py-3 rounded-md font-semibold hover:bg-blue-700 transition-colors"
            >
              Schedule a Consultation
            </Link>
            <Link
              to="/resources/education"
              className="bg-white text-blue-600 border border-blue-600 px-8 py-3 rounded-md font-semibold hover:bg-blue-50 transition-colors"
            >
              Learn More
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}